import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import {
  useGetManualServiceByKeyQuery,
  useUpdateManualServiceMutation,
  usePatchManualServiceMutation,
  useDeleteManualServiceMutation,
} from '../../../store/api/manualServicesApi';
import { formatDate } from '../../../utils/dateFormatter';

export default function ManualServiceDetailsPage() {
  const { t, i18n } = useTranslation();
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const currentLanguage = (i18n.language || 'ar') as 'ar' | 'en';

  const serviceKey = key || '';

  // Skip if key is "new" (should use AddManualServicePage instead)
  const { data, isLoading, error } = useGetManualServiceByKeyQuery(serviceKey, {
    skip: !serviceKey || serviceKey === 'new',
  });

  const [updateService, { isLoading: isUpdating }] = useUpdateManualServiceMutation();
  const [patchService, { isLoading: isPatching }] = usePatchManualServiceMutation();
  const [deleteService, { isLoading: isDeleting }] = useDeleteManualServiceMutation();

  const service = data?.data?.service;

  // Form states
  const [title, setTitle] = useState<string>('');
  const [titleEn, setTitleEn] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [descriptionEn, setDescriptionEn] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(true);
  const [maintenance, setMaintenance] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  
  // Validation constants
  const MAX_DESCRIPTION_LENGTH = 4096;

  useEffect(() => {
    if (service) {
      setTitle(service.title || '');
      setTitleEn(service.titleEn || '');
      setDescription(service.description || '');
      setDescriptionEn(service.descriptionEn || '');
      setVisible(service.visible);
      setMaintenance(service.maintenance);
    }
  }, [service]);

  const handleUpdate = async () => {
    if (!service) return;

    // Validate description length
    if (description.trim().length > MAX_DESCRIPTION_LENGTH) {
      toast.error(
        t('admin.manualServices.add.error.descriptionTooLong') ||
          `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`
      );
      return;
    }

    // Validate descriptionEn length
    if (descriptionEn.trim().length > MAX_DESCRIPTION_LENGTH) {
      toast.error(
        t('admin.manualServices.add.error.descriptionEnTooLong') ||
          `English description must not exceed ${MAX_DESCRIPTION_LENGTH} characters`
      );
      return;
    }

    try {
      await updateService({
        key: serviceKey,
        data: {
          title: title.trim(),
          titleEn: titleEn.trim() || undefined,
          description: description.trim() || undefined,
          descriptionEn: descriptionEn.trim() || undefined,
          visible,
          maintenance,
        },
      }).unwrap();

      toast.success(t('admin.manualServices.updateSuccess') || 'Manual service updated successfully');
      setIsEditing(false);
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update manual service');
    }
  };

  const handleToggleVisible = async () => {
    if (!service) return;

    try {
      await patchService({
        key: serviceKey,
        data: { visible: !service.visible },
      }).unwrap();

      toast.success(
        t('admin.manualServices.toggleVisibleSuccess') || `Service ${!service.visible ? 'shown' : 'hidden'} successfully`
      );
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to toggle visibility');
    }
  };

  const handleToggleMaintenance = async () => {
    if (!service) return;

    try {
      await patchService({
        key: serviceKey,
        data: { maintenance: !service.maintenance },
      }).unwrap();

      toast.success(
        t('admin.manualServices.toggleMaintenanceSuccess') ||
          `Service ${!service.maintenance ? 'removed from' : 'put in'} maintenance mode successfully`
      );
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to toggle maintenance mode');
    }
  };

  const handleDelete = async () => {
    if (!service) return;

    try {
      await deleteService(serviceKey).unwrap();
      toast.success(t('admin.manualServices.deleteSuccess') || 'Manual service deleted successfully');
      navigate('/admin/manual-services', { replace: true });
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to delete manual service');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !service) {
    return (
      <div className="px-2 sm:px-4 lg:px-0">
        <div className="text-center py-12">
          <p className="text-danger mb-4">
            {t('admin.manualServices.error.notFound') || 'Manual service not found'}
          </p>
          <Link
            to="/admin/manual-services"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold transition-all duration-200 hover:underline"
          >
            {t('admin.manualServices.back') || 'Back to Services'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header */}
      <div
        className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <div>
          <Link
            to="/admin/manual-services"
            className="text-brand-accent hover:text-brand-accent/80 font-semibold mb-4 inline-flex items-center gap-2 transition-all duration-200 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('admin.manualServices.back') || 'Back to Services'}
          </Link>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent">
            {t('admin.manualServices.details.title') || 'Manual Service Details'}
          </h1>
          <p className="text-neutral-medium text-sm sm:text-base mt-2">
            {t('admin.manualServices.details.subtitle') || 'View and manage manual service settings'}
          </p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          {!isEditing && (
            <>
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              >
                {t('admin.manualServices.details.edit') || 'Edit'}
              </button>
              <button
                onClick={() => setDeleteDialog(true)}
                className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/50 font-heading font-semibold rounded-lg hover:bg-red-500/30 transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              >
                {t('admin.manualServices.details.delete') || 'Delete'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Service Details */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-6 border border-neutral-dark/50 space-y-6"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        {!isEditing ? (
          <>
            {/* Read-only view */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.form.key') || 'Key'}
                </label>
                <p className="text-neutral-light font-mono text-sm">{service.key}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.form.title') || 'Title (Arabic/Default)'}
                </label>
                <p className="text-neutral-light">{service.title}</p>
              </div>
              {service.titleEn && (
                <div>
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.manualServices.form.titleEn') || 'Title (English)'}
                  </label>
                  <p className="text-neutral-light">{service.titleEn}</p>
                </div>
              )}
              {service.description && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.manualServices.form.description') || 'Description (Arabic)'}
                  </label>
                  <p className="text-neutral-light whitespace-pre-wrap">{service.description}</p>
                </div>
              )}
              {service.descriptionEn && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-neutral-medium mb-1">
                    {t('admin.manualServices.form.descriptionEn') || 'Description (English)'}
                  </label>
                  <p className="text-neutral-light whitespace-pre-wrap">{service.descriptionEn}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.form.visible') || 'Visible'}
                </label>
                <div className="flex items-center gap-2">
                  {service.visible ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                      {t('admin.manualServices.status.visible') || 'Visible'}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-500/20 text-gray-400 border border-gray-500/50">
                      {t('admin.manualServices.status.hidden') || 'Hidden'}
                    </span>
                  )}
                  <button
                    onClick={handleToggleVisible}
                    disabled={isPatching}
                    className="text-xs text-brand-accent hover:text-brand-accent/80 transition-all duration-200 hover:underline"
                  >
                    {t('admin.manualServices.details.toggle') || 'Toggle'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.form.maintenance') || 'Maintenance Mode'}
                </label>
                <div className="flex items-center gap-2">
                  {service.maintenance ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                      {t('admin.manualServices.status.maintenance') || 'In Maintenance'}
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded bg-green-500/20 text-green-400 border border-green-500/50">
                      {t('admin.manualServices.status.active') || 'Active'}
                    </span>
                  )}
                  <button
                    onClick={handleToggleMaintenance}
                    disabled={isPatching}
                    className="text-xs text-brand-accent hover:text-brand-accent/80 transition-all duration-200 hover:underline"
                  >
                    {t('admin.manualServices.details.toggle') || 'Toggle'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.details.created') || 'Created At'}
                </label>
                <p className="text-neutral-light text-sm">{formatDate(service.createdAt, 'PPpp', currentLanguage)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-medium mb-1">
                  {t('admin.manualServices.details.updated') || 'Updated At'}
                </label>
                <p className="text-neutral-light text-sm">{formatDate(service.updatedAt, 'PPpp', currentLanguage)}</p>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Edit form */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.manualServices.form.title') || 'Title (Arabic/Default)'} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.manualServices.form.titleEn') || 'Title (English)'}
                </label>
                <input
                  type="text"
                  value={titleEn}
                  onChange={(e) => setTitleEn(e.target.value)}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.manualServices.form.description') || 'Description (Arabic)'}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= MAX_DESCRIPTION_LENGTH) {
                      setDescription(value);
                    }
                  }}
                  rows={4}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent resize-none"
                />
                <p className="text-xs text-neutral-medium mt-1">
                  {description.length}/{MAX_DESCRIPTION_LENGTH} {t('common.characters') || 'characters'}
                  {description.length > MAX_DESCRIPTION_LENGTH * 0.9 && (
                    <span className="text-yellow-400 ml-1">
                      ({t('admin.manualServices.form.nearLimit') || 'Near limit'})
                    </span>
                  )}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-light mb-2">
                  {t('admin.manualServices.form.descriptionEn') || 'Description (English)'}
                </label>
                <textarea
                  value={descriptionEn}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= MAX_DESCRIPTION_LENGTH) {
                      setDescriptionEn(value);
                    }
                  }}
                  rows={4}
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent resize-none"
                />
                <p className="text-xs text-neutral-medium mt-1">
                  {descriptionEn.length}/{MAX_DESCRIPTION_LENGTH} {t('common.characters') || 'characters'}
                  {descriptionEn.length > MAX_DESCRIPTION_LENGTH * 0.9 && (
                    <span className="text-yellow-400 ml-1">
                      ({t('admin.manualServices.form.nearLimit') || 'Near limit'})
                    </span>
                  )}
                </p>
                <p className="text-xs text-neutral-medium mt-1">
                  {t('admin.manualServices.form.descriptionEnHint') ||
                    'If user language is English, this description will be displayed if available, otherwise Arabic description will be used as fallback'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={(e) => setVisible(e.target.checked)}
                      className="w-5 h-5 rounded border-neutral-medium bg-neutral-dark text-brand-accent focus:ring-brand-accent"
                    />
                    <span className="text-sm font-medium text-neutral-light">
                      {t('admin.manualServices.form.visible') || 'Visible'}
                    </span>
                  </label>
                </div>
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={maintenance}
                      onChange={(e) => setMaintenance(e.target.checked)}
                      className="w-5 h-5 rounded border-neutral-medium bg-neutral-dark text-brand-accent focus:ring-brand-accent"
                    />
                    <span className="text-sm font-medium text-neutral-light">
                      {t('admin.manualServices.form.maintenance') || 'Maintenance Mode'}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating
                    ? t('admin.manualServices.details.updating') || 'Updating...'
                    : t('admin.manualServices.details.save') || 'Save Changes'}
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    // Reset form to original values
                    if (service) {
                      setTitle(service.title || '');
                      setTitleEn(service.titleEn || '');
                      setDescription(service.description || '');
                      setDescriptionEn(service.descriptionEn || '');
                      setVisible(service.visible);
                      setMaintenance(service.maintenance);
                    }
                  }}
                  className="px-6 py-3 bg-neutral-medium text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-medium/80 transition-all duration-200 hover:scale-105 active:scale-95"
                >
                  {t('common.cancel') || 'Cancel'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={() => {
          void handleDelete();
        }}
        title={t('admin.manualServices.deleteTitle') || 'Delete Manual Service'}
        message={
          t('admin.manualServices.deleteConfirm') ||
          'Are you sure you want to delete this manual service? This action cannot be undone.'
        }
        confirmText={t('common.confirm') || 'Confirm'}
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
}



